import React, { useEffect, useMemo } from 'react';
import DataSourceContext from './DataSourceContext';
import {
  DataSourceProps, DataSourceStatePayload, ClientDataSourceProviderProps, SortDefinition,
} from './types';
import { dataReducer, initialState } from './reducer';
import {
  nextPage,
  prevPage,
  goToPage,
  setData,
  setError,
  setSearch,
  setPageSize,
  setSortBy,
} from './reducer/actions';

const defaultSearchFn = (items: any[], searchTerm: string) => items?.filter((item) => item.name.toLowerCase().includes(searchTerm.toLowerCase()));

export const ClientDataSource: React.FC<ClientDataSourceProviderProps> = ({
  children,
  data,
  paginate = false,
  searchFn = defaultSearchFn,
}) => {
  const [state, dispatch] = React.useReducer(dataReducer, initialState);
  const { currentPage: page, pageSize, searchTerm } = state;

  const value: DataSourceProps = useMemo(() => ({
    ...state,
    nextPage: () => dispatch(nextPage()),
    prevPage: () => dispatch(prevPage()),
    goToPage: (pageNumber: number) => dispatch(goToPage(pageNumber)),
    setData: (payload: DataSourceStatePayload) => dispatch(setData(payload)),
    setError: (error: string) => dispatch(setError(error)),
    search: (search: string) => dispatch(setSearch(search)),
    sort: (sortBy: SortDefinition) => dispatch(setSortBy(sortBy)),
    setPageSize: (size: number) => dispatch(setPageSize(size)),
  }), [state]);

  useEffect(() => {
    if (data) {
      let items = searchFn(data, searchTerm);
      let totalPages = Math.ceil(items.length / pageSize);

      if (paginate) {
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;

        items = items.slice(startIndex, endIndex);
        totalPages = Math.ceil((searchTerm ? items : data).length / pageSize);
      }

      value.setData({ totalPages, items });
    }
  }, [data, paginate, page, pageSize, searchTerm]);

  return (
    <DataSourceContext.Provider value={value}>
      {children}
    </DataSourceContext.Provider>
  );
};
